import { IAction } from '../../../../Assembler/dist/registry/iface/iAction.js';
import { JsonObject } from '../../../../Assembler/dist/app/json/jsonValue.js';
import type { GetParentJsonValueHandler } from '../../../../Assembler/dist/registry/iface/getParentJsonValueHandler.js';
import type { ITransientStore } from '../../../../Assembler/dist/registry/iface/iTransientStore.js';
import { Logger } from '../../../../Arshu/dist/logger/logger.js';
import { CounterManager } from '../dataReloadable/counterManager.js';
import { CounterFilter } from '../../registryCommon/dynamicData/counterFilter.js';

/**
 * Counter action implementation that increments/decrements a counter value
 *
 * The param reads, the value coercion, the transient key and the arithmetic all live in CounterFilter —
 * the shared pure producer — so this action and the counter data/client tiers cannot drift from one
 * another.
 */
export class CounterAction extends IAction {
    private _counterManager: CounterManager | null = null;

    /**
     * Wires the App_Data-backed counter store used for cross-request persistence.
     */
    initialize(counterManager: CounterManager): void {
        this._counterManager = counterManager;
    }

    /**
     * Gets the full type name for DynamicAction operation tracking
     */
    get typeName(): string {
        return 'CounterAction';
    }

    /**
     * Applies the requested counter mutation by recording the updated value in transient state.
     */
    doAction(
        appSite: string,
        appView: string,
        appFile: string,
        componentName: string,
        resolvedAppSite: string,
        resolvedComponentName: string,
        paramJson: JsonObject,
        componentJson: JsonObject,
        transientStore: ITransientStore,
        actionJson: JsonObject | null = null,
        getParentJsonValue: GetParentJsonValueHandler | null = null
    ): void {
        try {
            const counterKey = CounterFilter.readCounterKey(paramJson);
            const counterChange = CounterFilter.readCounterChange(paramJson);

            const persist = CounterFilter.readPersistFlag(paramJson);
            const storeCounterKey = CounterManager.buildCounterKey(appSite, componentName, counterKey);
            const storedCounter = this._counterManager !== null
                ? this._counterManager.tryGetValue(storeCounterKey)
                : { found: false, value: 0 };

            let currentCounter = 0;
            const transientCounterKey = CounterFilter.buildTransientCounterKey(componentName, counterKey);
            if (transientStore.containsKey(transientCounterKey) === true) {
                const transientCounterValue = transientStore.getValue(transientCounterKey);
                if (transientCounterValue !== null) {
                    currentCounter = CounterFilter.toCounterValue(transientCounterValue);
                }
            } else if (persist === true && storedCounter.found === true) {
                currentCounter = storedCounter.value;
            } else if (componentJson !== null && componentJson.containsKey(counterKey) === true) {
                currentCounter = CounterFilter.toCounterValue(componentJson.get(counterKey));
            }

            if (actionJson !== null) {
                if (actionJson.containsKey('Action') === true) {
                    currentCounter = CounterFilter.applyCounterAction(
                        currentCounter, actionJson.get('Action').toString(), counterChange);
                }
            }

            transientStore.setValue(transientCounterKey, currentCounter);
            if (persist === true && this._counterManager !== null) {
                this._counterManager.setValue(storeCounterKey, currentCounter);
            }
        } catch (ex) {
            Logger.error((ex as Error).message, 'CounterAction');
        }
    }
}
